import { IAsset, IMyBid, TBidRankStatus, TBidTag } from '@northladder/services';

import { IFetchAllBidsRawData, IFetchAllBidsData } from '../types';

interface IBidRankStatusFields {
  availableQuantity: number;
  isBidActive: boolean;
  isSpotSummit: boolean;
  rank: number;
}

/**
 * -----------------------------------------------------------------------------
 * Normalizes bid tag from API format (uppercase) to internal format (lowercase kebab-case).
 * Preserves all distinct tags including IN_QUEUE and my-bids as separate values.
 */
export function normalizeBidTag(tag: string | undefined): TBidTag {
  if (!tag) return 'no-bids';

  const tagMap: Record<string, TBidTag> = {
    WINNING: 'winning',
    RANK_1: 'rank-one',
    NOT_WINNING: 'not-winning',
    OUT_OF_QUANTITY: 'out-of-qty',
    NO_BID: 'no-bids',
    IN_QUEUE: 'in-queue',
    RUNNER_UP: 'runner-up',
  };

  // If uppercase format from API, convert it
  if (tag in tagMap) {
    return tagMap[tag] as TBidTag;
  }

  // If already lowercase, validate and return as-is
  const validTags: readonly TBidTag[] = [
    'winning',
    'rank-one',
    'runner-up',
    'in-queue',
    'not-winning',
    'out-of-qty',
    'no-bids',
    'my-bids',
  ] as const;

  if (validTags.includes(tag as TBidTag)) {
    return tag as TBidTag;
  }

  // Fallback
  return 'no-bids';
}

/**
 * Compute the bid rank status based on the bid users bid details. This allows
 * to do it once instead of doing it multiple times within component rerenders.
 * Only uses: rank, isSpotSummit, availableQuantity, isBidActive from the bid object.
 */
export function getBidRankStatus(
  bid: IBidRankStatusFields | null,
  isRankOneBidDisplayEnabled: boolean = false
): TBidRankStatus {
  // No bid or inactive bid
  if (!bid || !bid.isBidActive) return 'no-bids';

  const { availableQuantity, isSpotSummit, rank } = bid;

  if (isRankOneBidDisplayEnabled && rank === 1) return 'rank-one';

  // Winning range
  if (isSpotSummit || rank === 1) return 'winning';

  if (availableQuantity <= 0) return 'out-of-qty';

  return 'not-winning';
}

/**
 * TODO: Remove the rank summary from the response, as we are using standalone
 * rank api
 */
export function formatBidSearchResults(
  response: IFetchAllBidsRawData,
  page: number,
  isRankOneBidDisplayEnabled?: boolean
): IFetchAllBidsData {
  const cleanAssetList: IAsset[] = response.assets.map((asset) => {
    // NOTE: : Exiting early not to break types for some reason if the asset is null
    if (!asset) return {} as IAsset;

    let myBid: IMyBid | null = null;

    const {
      _id,
      attributes,
      brandId,
      brandName,
      brandLogo,
      categoryName,
      gapToFirstRank,
      hotDealFlag: isHotDeal = false,
      imageUrl,
      name,
      stepperValue,
      tag,
      topBid,
      lastUpdatedOn,
    } = asset;

    let assetNameStr = `${brandName || ''} ${name || ''} `;
    assetNameStr += (attributes ?? [])
      .map((attr) => `${attr.value}`)
      .join(', ');

    // TODO: Add arabic name later as iraq team needs english name for now.
    // let assetNameAr = `${brandName || ''} ${name || ''} `;
    // assetNameAr += attributes.map((attr) => `${attr.value_ar}`).join(', ');
    let assetNameES = `${brandName || ''} ${name || ''} `;
    assetNameES += (attributes ?? [])
      .map((attr) => `${attr.value_es}`)
      .join(', ');

    if (asset?.myBid) {
      const {
        autoTopupEnabled: isAutoTopupEnabled,
        availableQuantity,
        _id: bidId,
        isBidActive,
        isSpotSummit,
        maxQuantity,
        rank,
        spotPrice,
        updatedDate,
      } = asset.myBid;

      /**
       * If the auto topup is enabled, set the available quantity to 0.
       * Because if isAutoTopupEnabled true backend is giving default quantity as 5.
       * So in the UI we cannot select 5 as available quantity.
       */
      const formattedAvailableQuantity = isAutoTopupEnabled
        ? 0
        : availableQuantity;

      myBid = {
        availableQuantity: formattedAvailableQuantity ?? 0,
        bidId: bidId ?? '',
        isAutoTopupEnabled: isAutoTopupEnabled ?? false,
        isBidActive: isBidActive ?? false,
        isSpotSummit: isSpotSummit ?? false,
        /**
         * TODO:
         * @Rhea / @Renji - Please assign this value from the response
         */
        itemCondition: '',
        maxQuantity,
        rank,
        rankNumber: rank,
        spotPrice,
        // Keeping it here to avoid breaking changes
        topSpotPrice: topBid?.spotPrice || 0,
        updatedDate,
      };
    }

    const myBidPrice = myBid?.isBidActive ? myBid?.spotPrice || 0 : 0;

    const normalizedTag = normalizeBidTag(tag);
    const rankStatus = getBidRankStatus(myBid, isRankOneBidDisplayEnabled);

    const cleanAsset: IAsset = {
      assetId: _id ?? '',
      assetImage: imageUrl ?? '',
      assetName: {
        en: assetNameStr,
        // ar: assetNameAr || assetNameStr,
        ar: assetNameStr,
        es: assetNameES || assetNameStr,
      },
      assetNameLabel: assetNameStr,
      brandId,
      brandName: brandName || '',
      brandLogo: brandLogo ?? null,
      categoryName: categoryName || '',
      gapToFirstRank: gapToFirstRank ?? null,
      isHotDeal,
      lastUpdatedOn: lastUpdatedOn ?? 0,
      myBid,
      myBidPrice,
      myBidQuantity: myBid?.availableQuantity ?? 0,
      name: name || null,
      page,
      rankStatus,
      stepperValue: stepperValue || 0,
      rank: normalizedTag,
      topBidAmount: topBid?.spotPrice || 0,
      icon: 'other', // Compute dynamically based on category/brand/name
    };

    return cleanAsset;
  });

  return {
    assets: cleanAssetList,
    totalCount: response.totalCount,
    rankSummary: response.rankSummary,
    contextId: response.contextId,
  };
}
